import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Icons } from '../lib/Icons';

function topStrategies(profile) {
  const strategies = Array.isArray(profile?.recommendedProvision) ? profile.recommendedProvision : [];
  return strategies.filter((s) => typeof s === 'string' && s.trim()).slice(0, 6);
}

export default function StaffStrategyBriefs({ studentId, interventions = [] }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!studentId) return;
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'v2_student_profiles', studentId));
        setProfile(snap.exists() ? snap.data() : null);
      } catch (error) {
        console.warn('Could not load v2 profile for strategy briefs', error);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [studentId]);

  const strategies = useMemo(() => topStrategies(profile), [profile]);
  const tier = profile?.tierDecision?.tier || 'unassigned';
  const activeInterventions = (interventions || []).filter((i) => (i.status || 'active') === 'active');

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
        Loading staff strategy briefs...
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-slate-900 flex items-center">
        <Icons.LightBulb className="w-4 h-4 mr-2" />
        What staff should do tomorrow
      </h3>
      <p className="mt-1 text-xs text-slate-600">
        Role-specific quick guidance generated from current profile and intervention setup.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded border border-indigo-100 bg-indigo-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">SENCO focus</p>
          <ul className="mt-2 list-disc pl-4 text-xs text-indigo-900 space-y-1">
            <li>Tier status: {tier}</li>
            <li>Check APDR evidence snapshot before next review.</li>
            <li>Confirm Section F provision is visible in classroom practice.</li>
          </ul>
        </div>
        <div className="rounded border border-emerald-100 bg-emerald-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Class teacher focus</p>
          <ul className="mt-2 list-disc pl-4 text-xs text-emerald-900 space-y-1">
            {(strategies.length > 0 ? strategies : ['Apply agreed quality-first scaffolds and visual supports.'])
              .slice(0, 3)
              .map((s, idx) => (
                <li key={`teacher_${idx}`}>{s}</li>
              ))}
          </ul>
        </div>
        <div className="rounded border border-amber-100 bg-amber-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">TA focus</p>
          <ul className="mt-2 list-disc pl-4 text-xs text-amber-900 space-y-1">
            <li>Log session evidence immediately after delivery.</li>
            <li>Use consistent prompts and language agreed in provision.</li>
            <li>{activeInterventions.length} active intervention(s) to support this week.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
